<script setup lang="ts">
import {computed, reactive, ref} from 'vue';
import {downloadBlob} from '@/services/download';
import {Icon} from '@iconify/vue';
import {storeToRefs} from 'pinia';
import {useMusicUploadStore} from '@/stores/useMusicUploadStore';
import {useSettingsStore} from '@/stores/settings';
import {useNotificationStore} from '@/stores/notifications';
import type {VoiceMergeSettings} from '@/generated';


// 'compact' (Stepper-Empfehlung): nur der Anwenden-Button – Optionen + Ergebnisbereich entfallen.
const {variant = 'full'} = defineProps<{ variant?: 'full' | 'compact' }>()

const notificationStore = useNotificationStore();
const uploadStore = useMusicUploadStore();
const {currentFileId, analysisResult, isAnalyzing, isMerging, mergeResponse} = storeToRefs(uploadStore);

type MergeOptionKey = 'merge_parts' | 'remove_bass_parts' | 'flatten_voices';

// `merge_parts`/`flatten_voices` sind lokal-ephemere Toggles dieses Panels.
// `remove_bass_parts` verhält sich je nach Einsatzort bewusst unterschiedlich:
//  - full (Tab „Stimmen"): lokaler Toggle, Default AUS – bisheriges Merge-Verhalten unverändert.
//  - compact (Stepper/MusicXmlPreview): an die persistierte UI-Präferenz `settings.removeBassParts`
//    gebunden (gemerkt, geteilt mit der UploadCard-PDF-Checkbox).
const settingsStore = useSettingsStore();
const {removeBassParts} = storeToRefs(settingsStore);

const localSelection = reactive<Record<Exclude<MergeOptionKey, 'remove_bass_parts'>, boolean>>({
  merge_parts: true,
  flatten_voices: true,
});
const localBassRemove = ref(false);

// Effektiver Bass-Wert + Setter: compact liest/schreibt die Präferenz, full den lokalen Toggle.
function bassSelected(): boolean {
  return 'compact' === variant ? removeBassParts.value : localBassRemove.value;
}

function setBassSelected(value: boolean) {
  if ('compact' === variant) removeBassParts.value = value;
  else localBassRemove.value = value;
}

// Einheitlicher Lese-Zugriff für die generische Options-Schleife (Bass gesondert, Rest lokal).
function isSelected(key: MergeOptionKey): boolean {
  return 'remove_bass_parts' === key ? bassSelected() : localSelection[key];
}

// Statische Anzeige-Metadaten (keine Reaktivität nötig – daher const, kein computed).
const OPTIONS: ReadonlyArray<{ key: MergeOptionKey; icon: string; title: string; description: string }> = [
  {
    key: 'merge_parts',
    icon: 'fa6-solid:object-group',
    title: 'Stimmen zu Akkorden',
    description: 'Führt die zweite Stimme/Part als Akkorde in die erste zusammen',
  },
  {
    key: 'remove_bass_parts',
    icon: 'fa6-solid:filter',
    title: 'Bass-Parts entfernen',
    description: 'Entfernt Parts im Bass-Schlüssel',
  },
  {
    key: 'flatten_voices',
    icon: 'fa6-solid:layer-group',
    title: 'Stimmen flach machen',
    description: 'Löst mehrstimmige Voice-Container in Takten auf',
  },
];

// --- UI state ---
const mergeStatus = ref('');
const showStatus = ref(false);

const hasMergedResult = computed(() => !!mergeResponse.value?.music_xml);

// Stateless: das zusammengeführte MusicXML kommt inline; Download als clientseitiger Blob.
function downloadMergedXml() {
  const content = mergeResponse.value?.music_xml;
  if (!content) return;
  downloadBlob(new Blob([content], {type: 'application/xml'}), mergeResponse.value?.filename || 'merged.musicxml');
}

const nothingSelected = computed(
    () => !localSelection.merge_parts && !bassSelected() && !localSelection.flatten_voices,
);

function handleMergeStatus(message: string) {
  mergeStatus.value = message;
  showStatus.value = !!message;
}

function toggle(key: MergeOptionKey) {
  if ('remove_bass_parts' === key) setBassSelected(!bassSelected());
  else localSelection[key] = !localSelection[key];
}

async function applyMerge() {
  if (!currentFileId.value) {
    notificationStore.notify('Bitte zunächst eine Datei hochladen.', 'warning', 2000);
    return;
  }
  if (nothingSelected.value) {
    notificationStore.notify('Bitte mindestens eine Option auswählen.', 'warning', 2000);
    return;
  }



  handleMergeStatus('Stimmen werden zusammengeführt…');

  const settings: VoiceMergeSettings = {
    enabled: true,
    merge_parts: localSelection.merge_parts,
    remove_bass_parts: bassSelected(),
    flatten_voices: localSelection.flatten_voices,
  };

  // Der Store wendet das Zusammenführen an, macht das Ergebnis zur neuen Arbeitsbasis
  // (damit anschließend transponiert/verschoben/konvertiert werden kann) und erfasst es im Verlauf.
  const result = await uploadStore.applyVoiceMerge(settings);

  if (!result) {
    handleMergeStatus('');
    return;
  }

  handleMergeStatus('Zusammenführen abgeschlossen.');
  notificationStore.notify('Stimmen zusammengeführt.', 'success', 2000);
}
</script>

<template>
  <div class="max-w-full" v-if="analysisResult?.voices && analysisResult.voices > 1">
  <!-- Status Badge (Titel liefert der umschließende Tab) -->
  <div v-if="showStatus" class="mb-4 flex justify-end">
    <div class="px-3 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100 animate-fade-in">
      {{ mergeStatus }}
    </div>
  </div>

  <div class="space-y-2 mb-4" v-if="'full' === variant">
    <button
        v-for="opt in OPTIONS"
        :key="opt.key"
        type="button"
        class="group flex w-full items-center gap-2 p-2 rounded-xl border-2 transition-all duration-200 text-left"
        :class="isSelected(opt.key)
                  ? 'bg-emerald-50 border-emerald-300 shadow-sm'
                  : 'bg-white border-gray-200 hover:border-emerald-200 hover:shadow-md'"
        @click="toggle(opt.key)"
    >
      <div
          class="flex items-center justify-center w-10 h-10 rounded-lg transition-colors"
          :class="isSelected(opt.key)
                    ? 'bg-emerald-500 text-white'
                    : 'bg-gray-100 text-gray-500 group-hover:bg-emerald-100 group-hover:text-emerald-600'"
      >
        <Icon :icon="opt.icon" class="text-sm"/>
      </div>
      <div class="flex-1">
        <div class="font-semibold text-gray-800 text-sm">{{ opt.title }}</div>
        <div class="text-xs text-gray-500">{{ opt.description }}</div>
      </div>
      <Icon
          :icon="isSelected(opt.key) ? 'fa6-solid:square-check' : 'fa6-regular:square'"
          class="text-lg"
          :class="isSelected(opt.key) ? 'text-emerald-500' : 'text-gray-300'"
      />
    </button>
  </div>

  <!-- Kompakter Bass-Toggle (Stepper/compact): die übrigen Optionen bleiben auf ihren Defaults. -->
  <label
      v-if="'compact' === variant"
      class="mb-3 flex items-center gap-2 text-xs text-gray-700 cursor-pointer select-none"
      title="Entfernt Parts im Bass-Schlüssel, sodass die Arbeitsdatei einstimmig ist. Die fertige Griffschrift ist bauartbedingt immer einstimmig."
  >
    <input
        type="checkbox"
        class="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
        v-model="removeBassParts"
    />
    <span>Bass-Teil entfernen</span>
  </label>

  <!-- Action Buttons -->
  <div class="flex gap-2 ">
    <button
        id="applyVoiceMerge"
        class="btn btn-solid-fuchsia-400 hover:btn-mesh"
        type="button"
        :disabled="isMerging || isAnalyzing || nothingSelected"
        @click="applyMerge"
    >

      <Icon v-if="isMerging" icon="fa6-solid:spinner" class="animate-spin mr-2"/>
      <Icon v-else icon="fa6-solid:object-group" class="mr-2"/>
      {{ isMerging ? 'Führe zusammen…' : ('full' === variant ? 'Anwenden' : 'Stimmen zusammenführen') }}
    </button>
  </div>

  <!-- Result Section -->
  <div
      v-if="hasMergedResult && 'full' === variant"
      class="mt-6 p-5 bg-gradient-to-br from-emerald-50 to-green-50/30 rounded-xl border border-emerald-100/50 shadow-sm animate-fade-in-up"
  >
    <div class="flex items-center justify-between mb-4">
      <h3 class="font-semibold text-emerald-900 flex items-center gap-2">
        <div class="p-1.5 bg-emerald-100 rounded-md text-emerald-600">
          <Icon icon="fa6-solid:check"/>
        </div>
        Ergebnis verfügbar
      </h3>
      <span
          v-if="mergeResponse?.filename"
          class="text-xs text-emerald-700/70 truncate max-w-[150px]"
          :title="mergeResponse.filename"
      >
          {{ mergeResponse.filename }}
        </span>
    </div>

    <div class="flex flex-wrap gap-3">
      <button
          v-if="mergeResponse?.music_xml"
          type="button"
          @click="downloadMergedXml"
          class="btn bg-white text-emerald-700 border border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 shadow-sm flex items-center gap-2 flex-1 justify-center"
      >
        <Icon icon="fa6-solid:file-code"/>
        <span>MusicXML herunterladen</span>
      </button>
    </div>
  </div>
  </div>
</template>
